import * as React from 'react';
import hoistStatics from 'hoist-non-react-statics';
import { Location } from 'History';
import get from 'lodash/get';
import { authProvider } from 'dependencies/auth';
import { RouteObject } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { usePrompt } from 'app/components/prompt-unsaved/use-prompt';
import { RouterProps } from 'app/components/enhancers/with-router';
import { getHostingPath } from 'app/infrastructure/hosting';

export interface PromptProps {
  continue: (e: React.SyntheticEvent<any>) => void;
  cancel: () => void;
  shouldDisplayModal: boolean;
}

export interface WithPromptProps {
  wrappedComponentRef: () => any;
  shouldPrompt: boolean;
  promptText: string;
  onContinue?: () => void;
  route: RouteObject;
  router: RouterProps;
}
export interface WithPromptState {
  pendingRoute?: Location;
  internal?: boolean;
}

export const withPrompt = (Component: React.ComponentType<React.PropsWithChildren<any>>) => {

  const ComponentWithPrompt = (props: any) => {

    const [pendingRoute, setPendingRoute] = useState(undefined);
    const blockRoute = props.shouldPrompt;

    /**
     * Newer browsers does not show the error message due to security reasons
     */
    const onAppClose = (e: any) => {
      if (authProvider.redirectStarted || pendingRoute || !props.shouldPrompt) {
        return undefined;
      }
      e.preventDefault();
      const message = props.promptText;
      if (e) {
        e.returnValue = message;
      }
      return message;
    };

    const onLeave = (route: Location) => {
      // 16.4 HACK for connected cases - must be cleaned up as separate user story
      const isBackFromConnectedCase = get(route, 'action') === 'POP' && get(props, 'router.location.query.connectionTypeId');
      const navigatingInterally = get(route, 'location.state.internal') && !isBackFromConnectedCase;
      const isSaving = get(props, 'editing.submitting');
      const doNotShowPrompt = authProvider.redirectStarted || pendingRoute || !props.shouldPrompt || navigatingInterally || isSaving;
      if (doNotShowPrompt) {
        // do not prompt
        return true;

      }
      setPendingRoute(route);
      return false;
    };

    const continueWithRoute = () => {

      if (props.onContinue) {
        props.onContinue();
      }
      const { router } = props;
      const route = pendingRoute ?? {};
      const hostingPath = getHostingPath() || '';
      const nextRoute = (route.location?.pathname.startsWith(hostingPath)
        ? route.location?.pathname.substring(hostingPath.length)
        : route.location?.pathname) + route.location?.search;
      const { action } = pendingRoute;
      // TODO: investigate the underlying design problem
      // The history frame is "duplicated" into next history stack frame by connected cases with
      // a replace(), when returning from the newly connected case:
      // Initially, a history push is needed to open the connected case type selector (support browser-back from select modal).
      // Once you come back from a new CC we only want unique entries in the stack.
      const method: string = action === 'PUSH' ? 'to' : 'replace';
      router[method](nextRoute);
      setPendingRoute(undefined);
    };

    const cancel = () => {
      setPendingRoute(undefined);
      // required to restore the url
      props.router.replace(props.router.location);
    };

    usePrompt(onLeave, blockRoute);

    useEffect(() => {
      window.addEventListener('beforeunload', onAppClose);
      return () => {
        window.removeEventListener('beforeunload', onAppClose);
      };
    }, []);

    const { wrappedComponentRef, ...remainingProps } = props;

    return (
      <Component
        continue={continueWithRoute}
        cancel={cancel}
        shouldDisplayModal={!!pendingRoute}
        {...remainingProps}
        ref={wrappedComponentRef}
      />
    );
  };

  return hoistStatics(ComponentWithPrompt, Component);
};
